import { ChangeEvent, FunctionComponent, useState } from "react";

import { faChevronDown, faChevronRight, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FieldError } from "react-hook-form";

import { Icon } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { Section, Subsection } from "../schema/deep-research-prompt";

import { SubsectionEditor } from "./SubsectionEditor";

export interface SectionFieldError {
	header?: FieldError;
	instruction?: FieldError;
	subsections?: { header?: FieldError; instruction?: FieldError }[];
}

interface SectionEditorProps {
	sections: Section[];
	onChange: (sections: Section[]) => void;
	errors?: SectionFieldError[];
}

export const SectionEditor: FunctionComponent<SectionEditorProps> = ({
	sections,
	onChange,
	errors,
}) => {
	const [collapsedSections, setCollapsedSections] = useState<Record<number, boolean>>({});

	const toggleCollapse = (index: number) => {
		setCollapsedSections((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const handleAdd = () => {
		onChange([...sections, { order: sections.length + 1, header: "", instruction: "" }]);
	};

	const handleRemove = (index: number) => {
		const updated = sections.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }));

		onChange(updated);
	};

	const handleChange = (index: number, field: keyof Section, value: string | number) => {
		const updated = sections.map((s, i) => (i === index ? { ...s, [field]: value } : s));

		onChange(updated);
	};

	const handleSubsectionsChange = (index: number, subsections: Subsection[]) => {
		const updated = sections.map((s, i) =>
			i === index ? { ...s, subsections: subsections.length > 0 ? subsections : undefined } : s
		);

		onChange(updated);
	};

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<span className="text-sm font-semibold text-text-meta">Sections</span>
				<button
					className="flex items-center gap-1 text-xs text-blue-action hover:text-blue-action/80 cursor-pointer"
					type="button"
					onClick={handleAdd}
				>
					<Icon className="w-3 h-3" icon={faPlus} />
					Add Section
				</button>
			</div>
			{sections.map((section, index) => {
				const sectionHasErrors =
					!!errors?.[index]?.header ||
					!!errors?.[index]?.instruction ||
					errors?.[index]?.subsections?.some((sub) => !!sub?.header || !!sub?.instruction);

				return (
					<div
						key={index}
						className={`bg-white rounded-lg border shadow-sm overflow-hidden ${sectionHasErrors ? "border-red-400" : "border-border-light"}`}
					>
						<div
							className="flex items-center justify-between px-4 py-2 bg-brand-white cursor-pointer select-none"
							onClick={() => toggleCollapse(index)}
						>
							<div className="flex items-center gap-2">
								<Icon
									className="w-3 h-3 text-gray-400"
									icon={collapsedSections[index] ? faChevronRight : faChevronDown}
								/>
								<span className="text-sm font-medium text-text-meta">
									{section.header || `Section ${section.order}`}
								</span>
								<span className="text-xs text-text-disabled">#{section.order}</span>
								{sectionHasErrors && collapsedSections[index] && (
									<span className="text-xs text-red-500 font-medium">Missing required fields</span>
								)}
							</div>
							<button
								className="text-red-400 hover:text-red-600 cursor-pointer"
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleRemove(index);
								}}
							>
								<Icon className="w-3 h-3" icon={faTrash} />
							</button>
						</div>
						{!collapsedSections[index] && (
							<div className="p-4 flex flex-col gap-1">
								<InputField
									error={!!errors?.[index]?.header}
									handleChange={(e: ChangeEvent<HTMLInputElement>) =>
										handleChange(index, "header", e.target.value)
									}
									label="Header"
									name={`section-header-${index}`}
									placeholder="e.g. Executive Summary"
									value={section.header}
								/>
								{errors?.[index]?.header && (
									<span className="text-xs text-red-500 -mt-1">
										{errors[index].header?.message}
									</span>
								)}
								<InputField
									multiline
									error={!!errors?.[index]?.instruction}
									handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
										handleChange(index, "instruction", e.target.value)
									}
									label="Instruction"
									name={`section-instruction-${index}`}
									placeholder="Describe what this section should cover..."
									rows={4}
									value={section.instruction}
								/>
								{errors?.[index]?.instruction && (
									<span className="text-xs text-red-500 -mt-1">
										{errors[index].instruction?.message}
									</span>
								)}
								<SubsectionEditor
									errors={errors?.[index]?.subsections}
									subsections={section.subsections || []}
									onChange={(subs) => handleSubsectionsChange(index, subs)}
								/>
							</div>
						)}
					</div>
				);
			})}
			{sections.length === 0 && (
				<div className="text-center py-6 text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg">
					No sections added yet. Click &quot;Add Section&quot; to get started.
				</div>
			)}
		</div>
	);
};
